import type { Logger } from "@expert/logging";

import { trackFullStoryEvent } from "@expert/monitoring";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import ky from "ky";

import { defaultHeaders } from "../../guides/types";
import { type OutageApiItem, useOutageInfoStore } from "../store";

interface OutageResponse {
    outages: OutageApiItem[];
    allClear: boolean;
    timestamp: string;
}

export const useFetchOutageInfo = (logger: Logger) => {
    const { dispatcher } = useReactAnalytics();

    const fetchOutageInfo = async (ssoAccessToken: string) => {
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/api/outages/v1/active`;
            const response = await ky.get(url, {
                headers: {
                    ...defaultHeaders,
                    Authorization: `Bearer ${ssoAccessToken}`,
                },
                timeout: 30000,
            });
            const data = await response.json<OutageResponse>();

            const { outages: previousOutages, setOutages } = useOutageInfoStore.getState();

            // Detect changes before updating state
            const previousSlugs = new Set(Object.keys(previousOutages));
            const currentSlugs = new Set(data.outages.map((o) => o.slug));

            const newOutages = data.outages.filter((o) => !previousSlugs.has(o.slug));
            const clearedOutages = [...previousSlugs].filter(
                (slug) => !currentSlugs.has(slug) && previousOutages[slug]?.displayStatus === "active",
            );

            const hasChanges = newOutages.length > 0 || clearedOutages.length > 0;

            setOutages({ outages: data.outages, allClear: data.allClear, timestamp: data.timestamp });

            if (hasChanges || data.outages.length > 0) {
                const logMessage = hasChanges
                    ? `Outages | State changed: ${newOutages.length} new, ${clearedOutages.length} cleared`
                    : "Outages | Service returned active outages";

                logger.info(data, logMessage);

                const analyticsPayload = {
                    outagesCount: data.outages.length,
                    outages: data.outages.map((o) => o.slug),
                    allClear: data.allClear,
                };

                void dispatcher.dispatchBusinessEvent("Outage_InfoReceived", analyticsPayload);

                trackFullStoryEvent("Outage_InfoReceived", analyticsPayload);
            }

            return data;
        } catch (error) {
            logger.warn({ error }, "Outages | Error fetching active outages");

            const errorPayload = {
                error: error instanceof Error ? error.message : String(error),
            };

            void dispatcher.dispatchBusinessEvent("Outage_FetchError", errorPayload);

            trackFullStoryEvent("Outage_FetchError", errorPayload);

            return { outages: [], allClear: true, timestamp: new Date().toISOString() };
        }
    };

    return { fetchOutageInfo };
};
